import { UI } from '../../UI.ts'
import { Object3D, type Scene, type Object3DEventMap } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { SkeletonType, HandSkeletonType } from '../../enums/SkeletonType.js'
import { RigConfig } from '../../RigConfig.ts'
import type GLTFResult from './interfaces/GLTFResult.ts'
import { add_origin_markers, remove_origin_markers } from './OriginMarkerManager'
import { add_preview_skeleton, remove_preview_skeleton } from './PreviewSkeletonManager.ts'
import { HandHelper } from './HandHelper.ts'
import { CustomSkeletonStorage } from '../../../features/persistence/CustomSkeletonStorage'
import { materialize_skeleton, type SkeletonDescriptor } from '../../../features/skeleton/SkeletonDescriptor'

// Note: EventTarget is a built-ininterface and do not need to import it
export class StepLoadSkeleton extends EventTarget {
  private readonly loader: GLTFLoader = new GLTFLoader()
  private readonly ui: UI = UI.getInstance()
  private loaded_armature: Object3D = new Object3D()

  private _added_event_listeners: boolean = false
  private readonly _main_scene: Scene

  // used to help scale animations later
  // this is useful since position keyframes will need to be scaled
  // to prevent large offsets
  private skeleton_scale_percentage: number = 1.0

  // this was invented since this value is stored on a DOM element
  // this helps the marketing page set the type and doesn't rely on a DOM value
  // probably could refactor this a bit to be cleaner later.
  private manual_set_skeleton_type: SkeletonType = SkeletonType.None

  public skeleton_type (): SkeletonType {
    if (this.skeleton_file_path() === SkeletonType.None) {
      return this.manual_set_skeleton_type
    }

    return this.skeleton_file_path()
  }

  public set_skeleton_type (type: SkeletonType): void {
    this.manual_set_skeleton_type = type
  }

  // The edit skeleton step will use this to scale the skeleton when loading editable skeleton
  // animations listing will use this to scale all position keyframes
  public skeleton_scale (): number {
    return this.skeleton_scale_percentage
  }

  constructor (main_scene: Scene) {
    super()
    this._main_scene = main_scene
  }

  public begin (): void {
    if (this.ui.dom_current_step_element !== null) {
      this.ui.dom_current_step_element.innerHTML = 'Load Skeleton'
    }

    if (this.ui.dom_load_skeleton_tools !== null) {
      this.ui.dom_load_skeleton_tools.style.display = 'flex'
    }

    // if we are navigating back to this step, we don't want to add the event listeners again
    if (!this._added_event_listeners) {
      this.add_event_listeners()
      this._added_event_listeners = true
    }

    // when we come back to this step, there is a good chance we already selected a skeleton
    // so just use that and load the preview right when we enter this step
    if (!this.has_select_skeleton_ui_option()) {
      add_preview_skeleton(this._main_scene, this.skeleton_file_path(),
        this.hand_skeleton_type(), this.skeleton_scale_percentage).catch((err) => {
        console.error('error loading preview skeleton: ', err)
      })

    }

    // Initialize hand skeleton hand options visibility
    this.toggle_ui_hand_skeleton_options()

    // add origin markers for debugging model loading issues
    add_origin_markers(this._main_scene)

    // if there is a "select skeleton" option, disable proceeding
    // putting this check here helps us if we come back to this step later
    if (this.has_select_skeleton_ui_option()) {
      this.allow_proceeding_to_next_step(false)
    } else {
      this.allow_proceeding_to_next_step(true)
    }
  }

  public regenerate_origin_markers (): void {
    add_origin_markers(this._main_scene)
  }

  public dispose (): void {
    remove_origin_markers(this._main_scene)
    remove_preview_skeleton(this._main_scene)
  }

  private skeleton_file_path (): SkeletonType {
    // get currently selected option out of the model-selection drop-down
    if (this.ui.dom_skeleton_drop_type === null) return SkeletonType.None
    const skeleton_selection = this.ui.dom_skeleton_drop_type.options
    const skeleton_file: string = skeleton_selection[skeleton_selection.selectedIndex].value

    if (skeleton_file === 'select-skeleton') return SkeletonType.None
    // Custom skeletons are not part of SkeletonType — caller checks
    // is_custom_skeleton_selected() before going down this path.
    if (skeleton_file.startsWith('custom:')) return SkeletonType.None

    const config = RigConfig.by_key(skeleton_file)
    if (config === undefined) {
      console.error('unknown skeleton type selected: ', skeleton_file)
      return SkeletonType.Error
    }
    return config.skeleton_type
  }

  private hand_skeleton_type (): HandSkeletonType {
    const hand_selection = this.ui.dom_hand_skeleton_selection?.options
    if (hand_selection === undefined) return HandSkeletonType.AllFingers
    return hand_selection[hand_selection.selectedIndex].value as HandSkeletonType
  }

  private add_event_listeners (): void {
    // Populate the skeleton template dropdown from the central rig config plus
    // any custom skeletons the user has authored via Build Skeleton.
    if (this.ui.dom_skeleton_drop_type !== null) {
      void this.refresh_skeleton_dropdown()
    }

    // "+ Build New Skeleton" → engine handles transition.
    if (this.ui.dom_build_new_skeleton_button !== null) {
      this.ui.dom_build_new_skeleton_button.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('requestBuildSkeleton'))
      })
    }

    // Add event listener for skeleton type changes to show/hide hand options
    if (this.ui.dom_skeleton_drop_type !== null) {
      this.ui.dom_skeleton_drop_type.addEventListener('change', () => {
        this.toggle_ui_hand_skeleton_options()

        if (this.has_select_skeleton_ui_option()) {
          this.ui.dom_skeleton_drop_type?.options.remove(0)
        }

        if (this.ui.dom_scale_skeleton_controls !== null) {
          this.ui.dom_scale_skeleton_controls.style.display = 'flex'
        }

        // Custom skeletons skip the GLB preview path — there's no file to load,
        // and we just enable the proceed button so clicking it materializes the
        // descriptor from IDB.
        if (this.is_custom_skeleton_selected()) {
          remove_preview_skeleton(this._main_scene)
          this.allow_proceeding_to_next_step(true)
          return
        }

        add_preview_skeleton(this._main_scene, this.skeleton_file_path(), this.hand_skeleton_type(), this.skeleton_scale()).then(() => {
          this.allow_proceeding_to_next_step(true)
        }).catch((err) => {
          console.error('error loading preview skeleton: ', err)
        })
      })
    }

    if (this.ui.dom_load_skeleton_button !== null) {
      this.ui.dom_load_skeleton_button.addEventListener('click', () => {
        if (this.ui.dom_skeleton_drop_type === null) {
          console.warn('could not find skeleton selection drop down HTML element')
          return
        }

        // Custom skeleton selected — materialize from IDB rather than loading a GLB.
        const custom_id = this.selected_custom_skeleton_id()
        if (custom_id !== null) {
          void CustomSkeletonStorage.get(custom_id).then((descriptor) => {
            if (descriptor === null) {
              console.warn(`Custom skeleton ${custom_id} not found in storage.`)
              return
            }
            this.load_custom_skeleton(descriptor)
          })
          return
        }

        // Built-in skeleton selected — load its GLB.
        const rig_file = RigConfig.rig_file_for(this.skeleton_file_path())
        if (rig_file !== undefined) {
          this.load_skeleton_file(rig_file)
        }
      })
    }// end if statement

    // when hand skeleton type changes. update the preview skeleton
    this.ui.dom_hand_skeleton_selection?.addEventListener('change', () => {
      // rebuild the preview skeleton with the new hand skeleton type
      // make sure we keep existing scale if we made a change to that
      add_preview_skeleton(this._main_scene, this.skeleton_file_path(), this.hand_skeleton_type(), this.skeleton_scale()).catch((err) => {
        console.error('error loading preview skeleton: ', err)
      })
    })

    // scale skeleton controls
    this.ui.dom_scale_skeleton_input?.addEventListener('input', (event) => {
      // range sliders have rounding errors, so we round the value to avoid issues
      const new_value: number = Number((event.target as HTMLInputElement).value)
      this.update_skeleton_scale_to_value(new_value)
    })

    // reset the skeleton scale button
    this.ui.dom_reset_skeleton_scale_button?.addEventListener('click', () => {
      this.update_skeleton_scale_to_value(1.0)
    })

  }

  private update_skeleton_scale_to_value (new_value: number): void {
    this.skeleton_scale_percentage = Number(new_value)
    const display_value: string = Math.round(new_value * 100).toString() + '%'

    if (this.ui.dom_scale_skeleton_percentage_display !== null) {
      this.ui.dom_scale_skeleton_percentage_display.textContent = display_value
    }
    add_preview_skeleton(this._main_scene, this.skeleton_file_path(), this.hand_skeleton_type(), this.skeleton_scale_percentage)
      .catch((err) => {
        console.error('error loading preview skeleton: ', err)
      })
  }

  public load_skeleton_file (file_path: string): void {
    // load skeleton from GLB file
    this.loader.load(file_path, (gltf: GLTFResult) => {
      // traverse scene and find first bone object
      // we will go to the parent and mark that as the original armature
      let armature_found = false
      let original_armature: Object3D = new Object3D()

      gltf.scene.traverse((child: Object3D) => {
        // Note: three.js removes punctuation characters from names object names like `-` and `.` for sanitization
        // Our 3D source files will need to account fo this if we are relying on that later for parsing
        // https://discourse.threejs.org/t/avoid-dots-and-colons-being-deleted-from-models-name/15304/2
        if (child.type === 'Bone' && !armature_found) {
          armature_found = true

          if (child.parent != null) {
            original_armature = child.parent
          } else {
            console.warn('could not find armature parent while loading skeleton')
          }
        }
      })

      this.loaded_armature = original_armature.clone()
      this.loaded_armature.name = 'Loaded Armature'

      // Apply hand skeleton modifications for human skeletons
      if (this.skeleton_file_path() === SkeletonType.Human) {
        const helper = new HandHelper()
        helper.modify_hand_skeleton(this.loaded_armature, this.hand_skeleton_type())
      }

      this.loaded_armature.position.set(0, 0, 0)
      this.loaded_armature.updateWorldMatrix(true, true)

      // scale the armature to what we picked using the scale slider/preview
      this.loaded_armature.scale.set(this.skeleton_scale(), this.skeleton_scale(), this.skeleton_scale())

      this.dispatchEvent(new CustomEvent('skeletonLoaded', { detail: this.loaded_armature }))
    })
  }

  private has_select_skeleton_ui_option (): boolean {
    return this.ui.dom_skeleton_drop_type?.options[0]?.value === 'select-skeleton'
  }

  private allow_proceeding_to_next_step (allow: boolean): void {
    const btn = this.ui.dom_load_skeleton_button as HTMLButtonElement | null
    if (!btn) return
    btn.disabled = !allow // disable when not allowed
  }

  // returns a skeleton object that has been baked (applied) for scale
  public armature (): Object3D<Object3DEventMap> {
    return this.bake_scale_for_armature(this.loaded_armature)
  }

  // this does not mutate armature that goes in
  // bakes scale into bone positions and resets scale to 1
  private bake_scale_for_armature (armature: Object3D): Object3D {
    const scale = armature.scale.x // assumes uniform scale

    const cloned_armature: Object3D = armature.clone()

    // bake scale into all child bone positions
    if (scale !== 1) {
      cloned_armature.traverse((obj) => {
        if (obj instanceof Object3D && obj !== cloned_armature) {
          obj.position.multiplyScalar(scale)
        }
      })
      cloned_armature.scale.set(1, 1, 1)
    }

    cloned_armature.updateMatrixWorld(true)
    return cloned_armature
  }

  /**
   * Refresh the skeleton dropdown contents (built-ins + user's custom
   * skeletons). Called when the step begins and after a Build-Skeleton save.
   */
  public async refresh_skeleton_dropdown (): Promise<void> {
    if (this.ui.dom_skeleton_drop_type === null) return
    const previous_value = this.ui.dom_skeleton_drop_type.value
    // Populate the built-ins synchronously (before the first await) so the
    // dropdown is never empty: begin() inspects options[0] synchronously right
    // after kicking this off, and awaiting IDB first would leave it empty and
    // crash has_select_skeleton_ui_option().
    RigConfig.populate_skeleton_select(this.ui.dom_skeleton_drop_type, true)
    const customs = await CustomSkeletonStorage.list()
    RigConfig.populate_skeleton_select(this.ui.dom_skeleton_drop_type, true, customs)
    // Try to restore previous selection if it still exists.
    const has_previous = Array.from(this.ui.dom_skeleton_drop_type.options).some(o => o.value === previous_value)
    if (has_previous) this.ui.dom_skeleton_drop_type.value = previous_value
  }

  /**
   * Select a custom skeleton in the dropdown by id (called after a save in the
   * Build Skeleton flow so the user is dropped back here with their new
   * skeleton already chosen, ready to proceed to EditSkeleton).
   */
  public async select_custom_skeleton (id: string): Promise<void> {
    await this.refresh_skeleton_dropdown()
    if (this.ui.dom_skeleton_drop_type === null) return
    this.ui.dom_skeleton_drop_type.value = `custom:${id}`
    this.ui.dom_skeleton_drop_type.dispatchEvent(new Event('change'))
  }

  /** True when the currently-selected dropdown option is a `custom:${id}` entry. */
  public is_custom_skeleton_selected (): boolean {
    if (this.ui.dom_skeleton_drop_type === null) return false
    return this.ui.dom_skeleton_drop_type.value.startsWith('custom:')
  }

  /** Returns the custom skeleton id if currently selected, null otherwise. */
  public selected_custom_skeleton_id (): string | null {
    if (this.ui.dom_skeleton_drop_type === null) return null
    const value = this.ui.dom_skeleton_drop_type.value
    if (!value.startsWith('custom:')) return null
    return value.slice('custom:'.length)
  }

  /**
   * Materialize a SkeletonDescriptor into the same Object3D shape that
   * `load_skeleton_file` produces from a GLB, then dispatch `skeletonLoaded`
   * so the rest of the pipeline runs unchanged.
   */
  public load_custom_skeleton (descriptor: SkeletonDescriptor): void {
    const { armature } = materialize_skeleton(descriptor)
    armature.name = 'Loaded Armature'
    armature.position.set(0, 0, 0)
    armature.scale.set(this.skeleton_scale(), this.skeleton_scale(), this.skeleton_scale())
    armature.updateWorldMatrix(true, true)
    this.loaded_armature = armature
    this.dispatchEvent(new CustomEvent('skeletonLoaded', { detail: this.loaded_armature }))
  }

  private toggle_ui_hand_skeleton_options (): void {
    if (this.ui.dom_skeleton_drop_type === null || this.ui.dom_hand_skeleton_options === null) {
      return
    }

    const config = RigConfig.by_skeleton_type(this.skeleton_file_path())
    if (config?.has_hand_options === true) {
      this.ui.dom_hand_skeleton_options.style.display = 'flex'
    } else {
      this.ui.dom_hand_skeleton_options.style.display = 'none'
    }
  }
}
